import settings from '@/constants/constants';
import type { PatchPatientForInstantConsultationBodySchema } from '@montugroup/pms-api-contracts';
import { apiFormsContract } from '@montugroup/pms-api-contracts';
import { initQueryClient } from '@ts-rest/react-query';
import type { z } from 'zod';

type InstantConsultBody = z.input<typeof PatchPatientForInstantConsultationBodySchema>;

export type InstantConsultAttributes = InstantConsultBody['attributes'] &
  Required<Pick<InstantConsultBody['attributes'], 'intakeVariant'>>;

export type InstantConsultRequestInput = {
  attributes: InstantConsultAttributes;
  variant?: InstantConsultBody['variant'];
};

export type PatchPatientForInstantConsultationReply = z.infer<
  (typeof apiFormsContract.patchPatientForInstantConsultation.responses)[200]
>;

export const useInstantConsultRequest = () => {
  const tsRestClient = initQueryClient(apiFormsContract, {
    baseUrl: settings.baseUrl,
  });

  const { mutateAsync, ...rest } = tsRestClient.patchPatientForInstantConsultation.useMutation();

  return {
    ...rest,
    sendInstantConsultRequest: async ({ attributes, variant }: InstantConsultRequestInput) => {
      // react-query rejects non-2xx with a messageless error, so rethrow with the status.
      const result = await mutateAsync({
        body: {
          data: {
            type: 'patient',
            ...(variant ? { variant } : {}),
            attributes,
          },
        },
      }).catch((error: { status?: number }) => {
        throw new Error(`Failed to send SMS verification request due to an HTTP error: ${error?.status ?? 'unknown'}`);
      });

      if (result.status !== 200) {
        throw new Error(`Failed to send SMS verification request due to an HTTP error: ${result.status}`);
      }

      return result.body;
    },
  };
};
